import {
	NotFoundError,
	BadRequestError,
	UnprocessableError,
	MissingCredentialsError,
	InternalServerError,
	ServiceUnavailableError,
} from './errors';

const SUPPORTED_FILE_CONTENT_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'application/zip'];

const call = async (
	url: string,
	language: string,
	method: string,
	body?: any,
	formData?: FormData | null,
	opt?: any,
) => {
	const { headers: optHeaders, ...restOpt } = opt ?? {};
	const headers: Record<string, string> = {
		...normalizeHeaders(optHeaders),
		Accept: 'application/json',
		'Accept-Language': language,
	};

	if (body != null) {
		headers['Content-Type'] = 'application/json';
	}

	const request: RequestInit = {
		method,
		headers,
		credentials: 'include',
		...restOpt,
	};

	if (body != null && formData != null) {
		throw new Error(`cannot invoke call with both body and formData`);
	}

	if (body != null) {
		request.body = JSON.stringify(body);
	} else if (formData !== undefined) {
		request.body = formData;
	}

	let res: Response;
	try {
		res = await fetch(url, request);
	} catch (err) {
		const e = err as Error;
		if (e.name === 'AbortError') {
			throw err;
		}
		throw new Error(`error while fetching ${url}: ${e.message}`);
	}

	if (res.status !== 200) {
		switch (res.status) {
			case 500:
				throw new InternalServerError();
			case 400:
			case 404:
			case 422:
			case 503:
				let parsed = await res.json();
				let { code, message, cause } = parsed.error;
				if (res.status === 400) {
					throw new BadRequestError(message, cause);
				} else if (res.status === 404) {
					throw new NotFoundError(message);
				} else if (res.status === 422) {
					if (code === 'MissingCredentials') {
						throw new MissingCredentialsError();
					} else {
						throw new UnprocessableError(code, message, cause);
					}
				} else if (res.status === 503) {
					throw new ServiceUnavailableError(message);
				}
				break;
			default:
				throw new Error('Unknown error');
		}
	}

	const contentType = res.headers.get('content-type');
	if (!contentType) {
		return null;
	}

	let isSupportedFile = false;
	for (const type of SUPPORTED_FILE_CONTENT_TYPES) {
		if (contentType.indexOf(type) !== -1) {
			isSupportedFile = true;
			break;
		}
	}
	const isJSON = contentType === 'application/json';
	const isXML = contentType.includes('application/xml');
	if (!isSupportedFile && !isJSON && !isXML) {
		return null;
	}

	let data: any;
	if (isJSON) {
		try {
			data = await res.json();
		} catch (err) {
			const e = err as Error;
			throw new Error(`error while parsing json response from ${url}: ${e.message}`);
		}
	} else if (isXML) {
		try {
			return await res.text();
		} catch (err) {
			throw new Error(
				`error while parsing xml response from ${url}: ${(err as Error).message}`,
			);
		}
	} else {
		let blob: Blob;
		try {
			blob = await res.blob();
		} catch (err) {
			const e = err as Error;
			throw new Error(`error while converting image from ${url} to blob: ${e.message}`);
		}
		data = new File([blob], url.split('/').pop()!, { type: blob.type });
	}

	return data;
};

function normalizeHeaders(input: HeadersInit | undefined): Record<string, string> {
	if (input == null) {
		return {};
	}

	if (input instanceof Headers) {
		const result: Record<string, string> = {};
		input.forEach((value, key) => {
			result[key] = value;
		});
		return result;
	}

	if (Array.isArray(input)) {
		return Object.fromEntries(input);
	}

	return input as Record<string, string>;
}

export { call };
